// src/components/pages/SpareParts.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { jcb } from '../../api/apiClient';

const PER_PAGE = 12;
const CART_CONTEXT = { source: 'spare-parts-list' };
const CART_CONTEXT_KEY = 'spare-parts-list';

const SpareParts = () => {
    const { addSparePart, addMachineRequest, cartItems } = useCart();
    const navigate = useNavigate();

    // ============================================================
    // Listado principal + paginación
    // ============================================================
    const [results, setResults] = useState([]);
    const [areaFilter, setAreaFilter] = useState('');
    const [searchAreas, setSearchAreas] = useState([]);
    const [machineryTypes, setMachineryTypes] = useState([]);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const [loadingParts, setLoadingParts] = useState(true);
    const [listError, setListError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    // ============================================================
    // Formulario "no encuentras la refacción" → redirige a /contact
    // ============================================================
    const [contactForm, setContactForm] = useState({
        machineryType: '',
        model: '',
        year: '',
        area: '',
    });
    const [contactError, setContactError] = useState('');

    // ============================================================
    // Cargar filtros (áreas + tipos de maquinaria)
    // ============================================================
    useEffect(() => {
        let isMounted = true;
        const loadFilters = async () => {
            try {
                const { data } = await jcb.get('/spare-parts/filters');
                if (!isMounted) return;
                setSearchAreas(data.searchAreas || []);
                setMachineryTypes(data.machineryTypes || []);
            } catch (err) {
                console.error('Error cargando filtros:', err);
                if (isMounted) setListError('No se pudieron cargar los filtros.');
            } finally {
                if (isMounted) setLoadingFilters(false);
            }
        };
        loadFilters();
        return () => { isMounted = false; };
    }, []);

    // ============================================================
    // Cargar refacciones paginadas (query string manual)
    // ============================================================
    useEffect(() => {
        let isMounted = true;
        const loadParts = async () => {
            setLoadingParts(true);
            setListError('');
            try {
                const query = new URLSearchParams();
                query.append('page', currentPage);
                query.append('per_page', PER_PAGE);
                if (areaFilter) query.append('area', areaFilter);

                const { data } = await jcb.get(`/spare-parts/all?${query.toString()}`);
                if (!isMounted) return;

                setResults(data.results || []);
                setCurrentPage(data.current_page || 1);
                setLastPage(data.last_page || 1);
                setTotalResults(data.total || 0);
            } catch (err) {
                console.error('Error cargando refacciones:', err);
                if (isMounted) {
                    setListError('No se pudieron cargar las refacciones.');
                    setResults([]);
                }
            } finally {
                if (isMounted) setLoadingParts(false);
            }
        };
        loadParts();
        return () => { isMounted = false; };
    }, [areaFilter, currentPage]);

    // Reset a página 1 cuando cambia el área (solo si no está en 1)
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areaFilter]);

    // ============================================================
    // Carrito
    // ============================================================
    const isInCart = (id) =>
        cartItems.some(item =>
            item.itemType === 'sparePart'
            && item.id === id
            && item.contextKey === CART_CONTEXT_KEY
        );

    const handleAddAndGoToContact = (part) => {
        addSparePart(part, CART_CONTEXT);
        setTimeout(() => {
            window.location.href = '/contact';
        }, 100);
    };

    // ============================================================
    // Formulario → contacto
    // ============================================================
    const handleContactChange = (e) => {
        const { id, value } = e.target;
        setContactForm(prev => ({ ...prev, [id]: value }));
        setContactError('');
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        setContactError('');

        if (!contactForm.machineryType) {
            setContactError('El tipo de maquinaria es obligatorio.');
            return;
        }
        if (!contactForm.model.trim()) {
            setContactError('El modelo es obligatorio.');
            return;
        }
        if (!contactForm.year.trim()) {
            setContactError('El año es obligatorio.');
            return;
        }
        if (!contactForm.area) {
            setContactError('El área de búsqueda es obligatoria.');
            return;
        }

        addMachineRequest(contactForm);

        setTimeout(() => {
            navigate('/contact');
        }, 100);
    };

    // ============================================================
    // Paginación
    // ============================================================
    const goToPage = (page) => {
        if (page === currentPage) return;
        if (page < 1 || page > lastPage) return;
        setCurrentPage(page);
    };

    const getPageNumbers = () => {
        const pages = [];
        const range = 2;
        const start = Math.max(1, currentPage - range);
        const end = Math.min(lastPage, currentPage + range);

        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...');
        }
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < lastPage) {
            if (end < lastPage - 1) pages.push('...');
            pages.push(lastPage);
        }
        return pages;
    };

    const sparePartCount = cartItems.filter(i => i.itemType === 'sparePart').length;

    return (
        <div className="container mx-auto px-6 py-24">
            <nav className="text-sm mb-12">
                <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
                <span className="mx-2 text-text-muted">/</span>
                <span className="text-dark-primary font-medium">Refacciones</span>
            </nav>

            <div className="max-w-3xl mb-12">
                <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-primary mb-4">
                    Refacciones Originales
                </h1>
                <p className="text-text-muted text-lg">
                    Explora nuestro catálogo de refacciones JCB. Filtra por área para encontrar
                    exactamente lo que tu equipo necesita.
                </p>
            </div>

            {/* Filtro por área */}
            <div className="bg-surface-subtle p-6 md:p-8 mb-12 border border-border-subtle">
                <div className="flex flex-col md:flex-row md:items-end gap-6">
                    <div className="flex-1">
                        <label
                            className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2"
                            htmlFor="areaFilter"
                        >
                            Filtrar por Área
                        </label>
                        <select
                            id="areaFilter"
                            className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary focus:outline-none focus:border-dark-primary transition-all rounded-none"
                            value={areaFilter}
                            onChange={(e) => setAreaFilter(e.target.value)}
                            disabled={loadingFilters}
                        >
                            <option value="">
                                {loadingFilters ? 'Cargando áreas...' : 'Todas las áreas'}
                            </option>
                            {searchAreas.map((area) => (
                                <option key={area} value={area}>{area}</option>
                            ))}
                        </select>
                    </div>

                    {areaFilter && (
                        <button
                            type="button"
                            onClick={() => setAreaFilter('')}
                            className="font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-8 py-3.5 transition-all whitespace-nowrap"
                        >
                            Limpiar filtro
                        </button>
                    )}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-display font-bold text-dark-primary">
                        {areaFilter ? `Refacciones · ${areaFilter}` : 'Todas las refacciones'}
                    </h2>
                    {!loadingParts && !listError && (
                        <span className="text-sm text-text-muted">
                            {totalResults} {totalResults === 1 ? 'resultado' : 'resultados'}
                        </span>
                    )}
                </div>

                {listError && (
                    <div className="p-4 bg-red-50 border-l-4 border-red-500 text-sm text-red-700 mb-6">
                        {listError}
                    </div>
                )}

                {loadingParts ? (
                    <div className="text-center py-16">
                        <div className="inline-block w-12 h-12 border-4 border-jcb-yellow border-t-transparent rounded-full animate-spin"></div>
                        <p className="mt-4 text-text-muted">Cargando refacciones...</p>
                    </div>
                ) : results.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-dark-primary text-white">
                                        <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Refacción</th>
                                        <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Área</th>
                                        <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Precio</th>
                                        <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Disponibilidad</th>
                                        <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((part) => {
                                        const inCart = isInCart(part.id);
                                        return (
                                            <tr
                                                key={part.id}
                                                className="border-b border-border-subtle hover:bg-surface-subtle"
                                            >
                                                <td className="px-6 py-4">
                                                    <Link
                                                        to={`/spare-parts/${part.id}`}
                                                        className="font-display font-semibold text-dark-primary hover:text-jcb-yellow transition-colors"
                                                    >
                                                        {part.name}
                                                    </Link>
                                                    {part.description && (
                                                        <div className="text-xs text-text-muted mt-1 line-clamp-2">
                                                            {part.description}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-text-muted">
                                                    {part.area}
                                                </td>
                                                <td className="px-6 py-4 text-dark-primary font-medium">
                                                    {part.price > 0 ? (
                                                        <>
                                                            ${Number(part.price).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                            <span className="text-xs text-text-muted ml-1">USD</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-jcb-yellow text-sm font-medium">
                                                            A cotizar
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`text-xs font-medium px-3 py-1 ${part.availability === 'En stock'
                                                            ? 'bg-green-100 text-green-800'
                                                            : part.availability === 'Bajo pedido'
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : 'bg-red-100 text-red-800'
                                                            }`}
                                                    >
                                                        {part.availability}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col sm:flex-row gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => addSparePart(part, CART_CONTEXT)}
                                                            className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap"
                                                        >
                                                            {inCart ? 'Agregar +1' : 'Agregar'}
                                                        </button>
                                                        <span className="text-border-subtle hidden sm:inline">|</span>
                                                        {inCart ? (
                                                            <Link
                                                                to="/cart"
                                                                className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap"
                                                            >
                                                                Ver carrito
                                                            </Link>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddAndGoToContact(part)}
                                                                className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap"
                                                            >
                                                                Cotizar
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginación */}
                        {lastPage > 1 && (
                            <div className="mt-8 flex items-center justify-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    aria-label="Página anterior"
                                    className="w-10 h-10 flex items-center justify-center border border-border-medium text-dark-primary hover:border-jcb-yellow hover:text-jcb-yellow transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <span className="material-symbols-outlined text-base" aria-hidden="true">chevron_left</span>
                                </button>

                                {getPageNumbers().map((p, idx) =>
                                    p === '...' ? (
                                        <span
                                            key={`ellipsis-${idx}`}
                                            className="w-10 h-10 flex items-center justify-center text-text-muted"
                                        >
                                            …
                                        </span>
                                    ) : (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => goToPage(p)}
                                            className={`w-10 h-10 flex items-center justify-center font-display text-xs font-semibold transition-all ${currentPage === p
                                                ? 'bg-dark-primary text-white'
                                                : 'border border-border-medium text-dark-primary hover:border-jcb-yellow hover:text-jcb-yellow'
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}

                                <button
                                    type="button"
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === lastPage}
                                    aria-label="Página siguiente"
                                    className="w-10 h-10 flex items-center justify-center border border-border-medium text-dark-primary hover:border-jcb-yellow hover:text-jcb-yellow transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <span className="material-symbols-outlined text-base" aria-hidden="true">chevron_right</span>
                                </button>
                            </div>
                        )}

                        {sparePartCount > 0 && (
                            <div className="mt-6 flex justify-end">
                                <Link
                                    to="/cart"
                                    className="font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all"
                                >
                                    Ir a cotizar ({sparePartCount} items)
                                </Link>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-16 bg-surface-subtle border border-border-subtle">
                        <p className="text-text-muted text-lg">
                            {areaFilter
                                ? `No hay refacciones disponibles en el área "${areaFilter}".`
                                : 'No hay refacciones disponibles en este momento.'}
                        </p>
                    </div>
                )}
            </div>

            {/* ============================================================
                SECCIÓN: NO ENCUENTRAS LA REFACCIÓN
                ============================================================ */}
            <section className="mt-20 bg-dark-primary p-8 md:p-12 border border-dark-primary">
                <div className="max-w-3xl mx-auto text-center mb-10">
                    <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold block mb-3">
                        Búsqueda por Maquinaria
                    </span>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">
                        ¿No encuentras la refacción?
                    </h2>
                    <p className="text-neutral-300 max-w-md mx-auto">
                        Ingresa los datos de tu equipo JCB y te pondremos en contacto con un
                        especialista que te ayudará a localizarla.
                    </p>
                </div>

                <div className="max-w-3xl mx-auto bg-surface-subtle p-6 sm:p-10 border border-border-subtle">
                    <form
                        onSubmit={handleContactSubmit}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                        {contactError && (
                            <div className="md:col-span-2 p-4 bg-red-50 border-l-4 border-red-500 text-sm text-red-700">
                                {contactError}
                            </div>
                        )}

                        <div>
                            <label
                                className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2"
                                htmlFor="machineryType"
                            >
                                Tipo de Maquinaria *
                            </label>
                            <select
                                id="machineryType"
                                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary focus:outline-none focus:border-dark-primary transition-all rounded-none"
                                value={contactForm.machineryType}
                                onChange={handleContactChange}
                                disabled={loadingFilters}
                                required
                            >
                                <option value="">Seleccionar tipo...</option>
                                {machineryTypes.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2"
                                htmlFor="model"
                            >
                                Modelo *
                            </label>
                            <input
                                id="model"
                                type="text"
                                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none"
                                placeholder="Ej: 3CX, 455ZX, AJ48D..."
                                value={contactForm.model}
                                onChange={handleContactChange}
                                required
                            />
                        </div>

                        <div>
                            <label
                                className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2"
                                htmlFor="year"
                            >
                                Año *
                            </label>
                            <input
                                id="year"
                                type="text"
                                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none"
                                placeholder="Ej: 2020"
                                value={contactForm.year}
                                onChange={handleContactChange}
                                required
                            />
                        </div>

                        <div>
                            <label
                                className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2"
                                htmlFor="area"
                            >
                                Área de Búsqueda *
                            </label>
                            <select
                                id="area"
                                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary focus:outline-none focus:border-dark-primary transition-all rounded-none"
                                value={contactForm.area}
                                onChange={handleContactChange}
                                disabled={loadingFilters}
                                required
                            >
                                <option value="">Seleccionar área...</option>
                                {searchAreas.map((area) => (
                                    <option key={area} value={area}>{area}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
                            <button
                                type="submit"
                                className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-luxury font-semibold bg-jcb-yellow hover:bg-jcb-yellow-hover text-dark-primary px-8 py-4 transition-all"
                            >
                                <span>Continuar a contacto</span>
                                <span className="material-symbols-outlined text-sm" aria-hidden="true">arrow_forward</span>
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default SpareParts;